import { getAuthDetails } from '../../Common/cookiesHelper';
import { BASE_URL_CHATBOT } from '../../Constants/Config';
import { normalizeChatbotMerchantId } from '../../utils/chatbotConversationStorage';

const asField = (value) => {
  if (value == null || value === '') return '';
  return String(value).trim();
};

/** Resolve full chatbot URL as BASE_URL_CHATBOT + path (e.g. `chatbot/po/upload`). */
export const buildChatbotApiUrl = (path) => {
  const base = String(BASE_URL_CHATBOT || '')
    .trim()
    .replace(/\/+$/, '');
  const suffix = String(path || '')
    .trim()
    .replace(/^\/+/, '');
  return base ? `${base}/${suffix}` : '';
};

/**
 * Logged-in user email for chatbot Python APIs.
 * Superadmin uses dashboard email; merchant/manager/employee use session username
 * (same rule as Manage Employee / manager store lookups).
 */
export const getChatbotLoginUserEmail = () => {
  const { userTypeData, LoginGetDashBoardRecordJson, GetSessionLogin } =
    getAuthDetails();
  if (asField(userTypeData?.login_type).toLowerCase() === 'superadmin') {
    return asField(LoginGetDashBoardRecordJson?.data?.email);
  }
  return asField(
    GetSessionLogin?.username || LoginGetDashBoardRecordJson?.data?.email,
  );
};

/** Active store's display name (e.g. "Jenny Smoke Shop"), as shown in the header. */
export const getChatbotMerchantName = () => {
  const { LoginGetDashBoardRecordJson } = getAuthDetails();
  return asField(LoginGetDashBoardRecordJson?.data?.name);
};

/**
 * Auth + merchant fields required on every chatbot Python API request.
 * Reads live session cookies; merchant prefers the active chatbot merchant code.
 * Includes login_user_email on chat, PO upload, PO action, and PO save.
 */
export const getChatbotAuthFields = (merchantCode) => {
  const { userTypeData, LoginGetDashBoardRecordJson } = getAuthDetails();
  const fromContext = normalizeChatbotMerchantId(merchantCode);
  const fromSession = normalizeChatbotMerchantId(
    LoginGetDashBoardRecordJson?.data?.merchant_id,
  );

  return {
    token_id: asField(userTypeData?.token_id),
    login_type: asField(userTypeData?.login_type),
    session_id: asField(userTypeData?.session_id),
    merchant_id: fromContext || fromSession || '',
    login_user_email: getChatbotLoginUserEmail(),
  };
};

/** Shared headers for every chatbot Python API (Bearer + optional API key). */
export const getChatbotRequestHeaders = (contentType = 'application/json') => {
  const { userTypeData } = getAuthDetails();
  const token = asField(userTypeData?.token);
  const headers = {
    Accept: 'application/json',
  };
  if (contentType) {
    headers['Content-Type'] = contentType;
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const apiKey = process.env.REACT_APP_CHATBOT_API_KEY;
  if (apiKey) {
    headers['X-API-Key'] = apiKey;
  }
  return headers;
};

/** Append auth fields onto FormData (e.g. PO PDF upload). */
export const appendChatbotAuthFields = (form, merchantCode) => {
  const fields = getChatbotAuthFields(merchantCode);
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== '') form.append(key, value);
  });
  return fields;
};
